// detectDiningMoments — pure detection of "the traveler has free time at
// their destination right now" windows, for proactively surfacing a dining
// suggestion instead of requiring the traveler to think to ask a chatbot.
//
// Nothing like this existed before: the only prior "gap" concept
// (computeTripGaps.js) detects missing bookings (no hotel / no transport),
// not schedule gaps, and explicitly excludes dining. This reads the
// AT_DESTINATION window journeyStageEngine's resolveTripStages() now
// resolves (see that file's `resolveAtDestinationOverride`) as the single
// source of truth for "when is the traveler at their destination," then
// finds free intervals inside it by subtracting every other scheduled
// event (hotel, car rental, meeting, existing dining reservations).
//
// No destination-timezone infrastructure exists anywhere in this app today
// (confirmed during investigation), so meal-time bands are evaluated
// against the device's local clock — the same assumption every other
// journey/time computation in this codebase already makes.

import { STAGES } from '../../services/journeyStageEngine';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

// A free interval must overlap a meal band by at least this much to count
// as a real dining opportunity — long enough to plan around, short of the
// full band so a sliver at the edge of a gap doesn't manufacture a
// suggestion nobody could actually act on.
const MIN_MEAL_OVERLAP_MS = 60 * MINUTE_MS;

// A free window starting within this long of arriving at the destination
// is still "at the airport" in spirit, even once ground transport has
// technically ended.
const AIRPORT_CONTEXT_WINDOW_MS = 2 * HOUR_MS;

// How close a meeting's end/start has to be to a free interval's edge to
// count as "adjacent to a meeting" for location-context purposes.
const MEETING_ADJACENCY_MS = 30 * MINUTE_MS;

// Meal bands as local-clock hour-of-day ranges, checked in this priority
// order. Deliberately simple fixed bands rather than a scheduling-optimal
// search — this only needs to answer "is now (or the next free stretch) a
// reasonable time to suggest a meal," not plan an itinerary.
const MEAL_BANDS = [
  { mealType: 'breakfast', startHour: 6, endHour: 10 },
  { mealType: 'lunch', startHour: 11.5, endHour: 14 },
  { mealType: 'dinner', startHour: 17.5, endHour: 21 },
];

const SUGGESTED_RADIUS_MILES = {
  airport: 3,
  'near-meeting': 2,
  hotel: 5,
  unknown: 5,
};

function toMs(value) {
  if (value == null) return NaN;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return ms;
}

/**
 * Merge a sorted array of {startMs, endMs} intervals, combining any that
 * overlap or touch.
 */
function mergeIntervals(sorted) {
  const merged = [];
  for (const interval of sorted) {
    const last = merged[merged.length - 1];
    if (last && interval.startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, interval.endMs);
    } else {
      merged.push({ startMs: interval.startMs, endMs: interval.endMs });
    }
  }
  return merged;
}

/**
 * Free intervals = the complement of `busy` within [rangeStartMs, rangeEndMs].
 * `busy` must already be sorted and merged.
 */
function computeFreeIntervals(busy, rangeStartMs, rangeEndMs) {
  const free = [];
  let cursor = rangeStartMs;
  for (const b of busy) {
    if (b.startMs > cursor) {
      free.push({ startMs: cursor, endMs: Math.min(b.startMs, rangeEndMs) });
    }
    cursor = Math.max(cursor, b.endMs);
    if (cursor >= rangeEndMs) return free;
  }
  if (cursor < rangeEndMs) {
    free.push({ startMs: cursor, endMs: rangeEndMs });
  }
  return free;
}

/**
 * Finds the first meal band (breakfast → lunch → dinner, in that priority
 * order) that this interval overlaps by at least MIN_MEAL_OVERLAP_MS, on
 * the calendar day the interval starts. Returns the clipped overlap window
 * (never earlier than `nowMs`) plus the meal type, or null.
 */
function findMealWindow(interval, nowMs) {
  const dayStart = new Date(interval.startMs);
  dayStart.setHours(0, 0, 0, 0);
  const dayStartMs = dayStart.getTime();

  for (const band of MEAL_BANDS) {
    const bandStartMs = dayStartMs + band.startHour * HOUR_MS;
    const bandEndMs = dayStartMs + band.endHour * HOUR_MS;
    const overlapStart = Math.max(interval.startMs, bandStartMs, nowMs);
    const overlapEnd = Math.min(interval.endMs, bandEndMs);
    if (overlapEnd - overlapStart >= MIN_MEAL_OVERLAP_MS) {
      return { mealType: band.mealType, windowStart: overlapStart, windowEnd: overlapEnd };
    }
  }
  return null;
}

/**
 * 'airport' near arrival, 'near-meeting' when a meeting event sits right
 * before/after the free interval, 'hotel' once the trip has a hotel booked,
 * else 'unknown'.
 */
function deriveLocationContext({ trip, arrivedAtMs, events, interval }) {
  if (interval.startMs - arrivedAtMs <= AIRPORT_CONTEXT_WINDOW_MS) {
    return 'airport';
  }

  const hasAdjacentMeeting = (Array.isArray(events) ? events : []).some((event) => {
    if (event.type !== 'meeting') return false;
    const startMs = toMs(event.scheduledStart);
    const endMs = event.scheduledEnd ? toMs(event.scheduledEnd) : startMs;
    if (Number.isNaN(startMs)) return false;
    const safeEndMs = Number.isNaN(endMs) ? startMs : endMs;
    return (
      Math.abs(safeEndMs - interval.startMs) <= MEETING_ADJACENCY_MS ||
      Math.abs(startMs - interval.endMs) <= MEETING_ADJACENCY_MS
    );
  });
  if (hasAdjacentMeeting) return 'near-meeting';

  if (Array.isArray(trip?.hotels) && trip.hotels.length > 0) return 'hotel';

  return 'unknown';
}

/**
 * Detect a dining moment: a free interval, at the destination, right now
 * (or in the next qualifying gap), that overlaps a mealtime band.
 *
 * @param {Object|null} trip - Full trip object (for hasHotel context)
 * @param {Object|null} activeStage - `resolveTripStages(...).activeStage`.
 *   Only ever produces a result when this is the AT_DESTINATION stage.
 * @param {Array<Object>} events - `buildJourneyEvents(...).events` — already
 *   sorted, already-normalized hotel/carRental/meeting/dining/stage events.
 * @param {Date} now
 * @returns {{
 *   windowStart: Date, windowEnd: Date,
 *   mealType: 'breakfast'|'lunch'|'dinner',
 *   locationContext: 'airport'|'hotel'|'near-meeting'|'unknown',
 *   suggestedRadius: number,
 * }|null}
 */
export function detectDiningMoments(trip, activeStage, events, now) {
  if (!activeStage || activeStage.stage !== STAGES.AT_DESTINATION) return null;
  if (!(now instanceof Date) || Number.isNaN(now.getTime())) return null;

  const arrivedAtMs = toMs(activeStage.meta?.arrivedAt);
  const boundDate = activeStage.meta?.returnsAt || activeStage.meta?.tripEndsAt;
  const windowEndMs = toMs(boundDate);
  if (Number.isNaN(arrivedAtMs) || Number.isNaN(windowEndMs)) return null;

  const nowMs = now.getTime();
  const searchStartMs = Math.max(arrivedAtMs, nowMs);
  if (searchStartMs >= windowEndMs) return null;

  const busy = (Array.isArray(events) ? events : [])
    .filter((event) => event.type !== 'stage')
    .map((event) => {
      const startMs = toMs(event.scheduledStart);
      if (Number.isNaN(startMs)) return null;
      const endMs = event.scheduledEnd ? toMs(event.scheduledEnd) : startMs;
      return { startMs, endMs: Number.isNaN(endMs) ? startMs : endMs };
    })
    .filter((b) => b && b.endMs > searchStartMs && b.startMs < windowEndMs)
    .sort((a, b) => a.startMs - b.startMs);

  const freeIntervals = computeFreeIntervals(mergeIntervals(busy), searchStartMs, windowEndMs);

  for (const interval of freeIntervals) {
    const meal = findMealWindow(interval, nowMs);
    if (!meal) continue;

    const locationContext = deriveLocationContext({ trip, arrivedAtMs, events, interval });
    return {
      windowStart: new Date(meal.windowStart),
      windowEnd: new Date(meal.windowEnd),
      mealType: meal.mealType,
      locationContext,
      suggestedRadius: SUGGESTED_RADIUS_MILES[locationContext],
    };
  }

  return null;
}

export default detectDiningMoments;
